import * as React from 'react';

import { HelpPopover, type ChipTone } from '@/design';
import type { IAdminParticipantFact } from '../../api/participant-contracts';
import type { FactReading } from '../../registries';

/**
 * What the answers of `READING_OF_FACT_KEY` say (SR-416, the block 2 plan's B2-6): Veritant's
 * readings of the participant's own person answers, a team's or firm's members looked up, and the
 * CRD a respondent typed into the FSP survey — each in words, under a group of its own in Registry
 * answers, the payload still folded under "As stored" as every answer is.
 *
 * **A fact names no number** (survey-api serves the newest per `(sourceSystem, factKey)`, with no
 * `idType` or `value`), so these words never say a number was asked: a member is named from the
 * payload's own `member: {crd, slot}`, a typed CRD from `u4.respondent`'s `crd`, and FINRA's and
 * IAPD's envelopes about a typed CRD as "the CRD typed". Reading those two payload members is
 * the one place this page picks a member out of a payload — they are survey-api's own members,
 * written beside the registry's answer to say whom it is about, not the registry's record.
 *
 * **The tone is a reading, not a verdict**: at a firm on the wirehouse list is neither good nor
 * bad (`neutral`), disclosures on file want attention (`attention`), and a reading that failed is
 * the registries' failure (`danger`).
 */

const WIREHOUSE = 'firm.list_match.wirehouse';
const U4_KEYS: ReadonlySet<string> = new Set(['u4.disclosures', 'u4.member', 'u4.respondent']);

/** survey-api's `DisclosureOrigins` in a `u4.*` payload's `sources`, as the hub names them. */
const SOURCE_WORDS: Readonly<Record<string, string>> = {
  brokercheck: 'BrokerCheck',
  iapd: 'IAPD'
};

/** The registries' names a reading's answer came from, by `sourceSystem`. */
const REGISTRY_WORDS: Readonly<Record<string, string>> = {
  brokercheck: 'FINRA BrokerCheck',
  iapd: 'SEC IAPD'
};

/** An object's members, or none for anything else. */
function members(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** A member a payload holds as a string or a number, as text; null for anything else. */
function text(value: unknown): Nullable<string> {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
}

/** "member CRD 1234567, slot 2", from the payload's `member` — or "a member" where it names none. */
function memberWords(payload: unknown): string {
  const member = members(members(payload).member);
  const crd = text(member.crd);
  const slot = text(member.slot);
  if (crd === null) return 'a member';
  return slot === null ? `member CRD ${crd}` : `member CRD ${crd}, slot ${slot}`;
}

/** The firms a wirehouse reading matched, by name, the FINRA id beside. */
function matchedFirms(payload: unknown): string {
  const matched = members(payload).matched;
  if (!Array.isArray(matched) || matched.length === 0) return 'a firm the answer does not name';
  return matched
    .map((firm) => {
      const { firm_id: id, firm_name: name } = members(firm);
      const named = text(name);
      const numbered = text(id);
      if (named === null) return numbered === null ? 'a firm with no name' : `firm ${numbered}`;
      return numbered === null ? named : `${named} (${numbered})`;
    })
    .join(', ');
}

/** "Disclosures on file: 2 (BrokerCheck)" — the count the record lists, and who flags them. */
function disclosureWords(payload: unknown): string {
  const { count, sources } = members(payload);
  const listed = typeof count === 'number' && count > 0 ? String(count) : null;
  const from = Array.isArray(sources)
    ? sources.map((source) => SOURCE_WORDS[String(source)] ?? String(source)).join(', ')
    : '';
  const head = listed
    ? `Disclosures on file: ${listed}`
    : 'Disclosures on file — flagged by the registry, none listed';
  return from ? `${head} (${from})` : head;
}

const FAILED = 'Could not be read — the registry lookup failed.';

/**
 * `not_found` on a U4 reading — survey-api writes it when the records list no disclosure **and**
 * when neither registry knows the person (`U4Disclosures`), and the payload cannot tell the two
 * apart, so the words say both.
 */
const NO_DISCLOSURES = 'No disclosures on file — or no registry holds a record of them';

/** A status this app has no words for, as spelled — never read as a negative. */
function unworded(fact: IAdminParticipantFact): string {
  return `The system read “${fact.status as string}”, which this app has no words for.`;
}

/** `not_found` on the wirehouse reading — with no current firm known (`firm_ids` empty, which is
 *  also what a person nobody knows reads as), said so rather than as a firm off the list. */
function offTheList(payload: unknown): string {
  const ids = members(payload).firm_ids;
  return Array.isArray(ids) && ids.length === 0
    ? 'Not at a firm on the wirehouse list — no current firm is known for them.'
    : 'Not at a firm on the wirehouse list.';
}

/** What a `veritant` reading of the participant's own answers says, by status. */
function veritantWords(fact: IAdminParticipantFact): string {
  switch (fact.status) {
    case 'error':
      return FAILED;
    case 'ok':
      return fact.factKey === WIREHOUSE
        ? `At a firm on the wirehouse list: ${matchedFirms(fact.payload)}.`
        : `${disclosureWords(fact.payload)}.`;
    case 'not_found':
      return fact.factKey === WIREHOUSE ? offTheList(fact.payload) : `${NO_DISCLOSURES}.`;
    default:
      return unworded(fact);
  }
}

/** A U4 reading about somebody else's CRD, prefixed with whom it is about. */
function aboutWords(about: string, fact: IAdminParticipantFact): string {
  let said: string;
  switch (fact.status) {
    case 'error':
      said = 'could not be read — the registry lookup failed';
      break;
    case 'ok':
      said = disclosureWords(fact.payload).replace(/^Disclosures/, 'disclosures');
      break;
    case 'not_found':
      said = NO_DISCLOSURES.replace(/^No/, 'no');
      break;
    default:
      return unworded(fact);
  }
  return `${about.charAt(0).toUpperCase()}${about.slice(1)}: ${said}.`;
}

/** A registry's own envelope about somebody else's CRD — no summary, so its sentence and its
 *  payload. */
function envelopeWords(who: string, about: string, fact: IAdminParticipantFact): string {
  switch (fact.status) {
    case 'ok':
      return `${who} answered with a record about ${about} — below, exactly as it was stored.`;
    case 'not_found':
      return `${who} holds no record of ${about}.`;
    case 'error':
      return (
        `${who} could not be asked about ${about} — which says nothing about it. Nothing on ` +
        'this page asks again.'
      );
    default:
      return `${who} answered “${fact.status as string}” about ${about}, which this app has no words for.`;
  }
}

/** What one answer of a reading group says. */
export function readingWords(reading: FactReading, fact: IAdminParticipantFact): string {
  const who = REGISTRY_WORDS[fact.sourceSystem] ?? fact.sourceSystem;
  switch (reading) {
    case 'veritant':
      return veritantWords(fact);
    case 'member': {
      const about = memberWords(fact.payload);
      return U4_KEYS.has(fact.factKey) ? aboutWords(about, fact) : envelopeWords(who, about, fact);
    }
    case 'respondent': {
      if (U4_KEYS.has(fact.factKey)) {
        const crd = text(members(fact.payload).crd);
        return aboutWords(crd === null ? 'the CRD typed' : `CRD ${crd}`, fact);
      }
      return envelopeWords(who, 'the CRD typed', fact);
    }
  }
}

/** The status chip's tone for a U4 or wirehouse reading; undefined for a registry's envelope,
 *  which keeps the registries' own badge. */
export function readingTone(fact: IAdminParticipantFact): ChipTone | undefined {
  const u4 = U4_KEYS.has(fact.factKey);
  if (!u4 && fact.factKey !== WIREHOUSE) return undefined;
  if (fact.status === 'error') return 'danger';
  return fact.status === 'ok' && u4 ? 'attention' : 'neutral';
}

/** What a reading group is, behind a "?" beside its heading (SR-474) — an explanation, which a
 *  regular reader does not need on the screen. */
export function ReadingHelp({ label, reading }: { label: string; reading: FactReading }) {
  return (
    <HelpPopover label={`About ${label}`}>
      <p>{READING_HELP[reading]}</p>
    </HelpPopover>
  );
}

const READING_HELP: Record<FactReading, string> = {
  veritant:
    'What the system reads from this participant’s latest BrokerCheck and IAPD answers. Each is ' +
    'read again when a survey is started or reopened, and when the registries answer a lookup.',
  member:
    'The members looked up from inside a response. Only the newest answer of each kind is held, ' +
    'whichever member it was about.',
  respondent:
    'The CRD the respondent typed into the FSP survey, asked of both registries. It is not one of ' +
    'this participant’s numbers.'
};

/** What stays on the screen under a reading group's heading: the CRD the FSP respondent's answers
 *  were about — a fact of the page, not an explanation — and nothing for the other groups. */
export function ReadingNote({
  reading,
  facts
}: {
  reading: FactReading;
  facts: IAdminParticipantFact[];
}) {
  if (reading !== 'respondent') return null;
  const u4 = facts.find((fact) => fact.factKey === 'u4.respondent');
  const crd = u4 ? text(members(u4.payload).crd) : null;
  return (
    <p className="text-sm text-muted-foreground">
      {crd === null ? 'The registries’ answers name no number.' : `The last one read: CRD ${crd}.`}
    </p>
  );
}
